// Hồ sơ bắt buộc của đơn vị (Shop / CTV / Đại lý) — GET /onboarding/required.
// Chưa đủ thì giao diện chặn ở màn "Hoàn tất thông tin tài khoản" (/onboarding);
// server cũng chặn tạo đơn / nhập đơn / tạo cấp dưới bằng lỗi 409 `profile.incomplete`.

import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError, get } from './api'
import { queryClient } from './queryClient'
// Vòng import auth ↔ onboarding an toàn: chỉ gọi getAuthState (khai báo hàm) lúc chạy, không lúc nạp module
import { getAuthState } from './auth'

export type ProfileStepKey = 'owner' | 'warehouse' | 'bank'

export interface ProfileItem {
  key: ProfileStepKey
  label: string
  done: boolean
  missing: string[]
  link: string
}

export interface ProfileStatus {
  complete: boolean
  items: ProfileItem[]
  /** Chủ tài khoản hiện tại — điền sẵn form bước 1 */
  owner?: { name: string | null; phone: string | null; email: string | null } | null
}

/** Khoá gốc — invalidate bằng khoá này sau mỗi lần lưu hồ sơ / kho / ngân hàng. */
export const PROFILE_QUERY_KEY = ['onboarding-required'] as const

export const profileQueryKey = (shopId: string | null | undefined) => [...PROFILE_QUERY_KEY, shopId ?? 'none'] as const

export const fetchProfileStatus = () => get<ProfileStatus>('/onboarding/required')

/** Gọi sau khi lưu bất kỳ phần nào của hồ sơ — màn chặn và checklist cập nhật ngay. */
export function invalidateProfileStatus() {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  ])
}

/** Đơn vị quản trị (admin / super admin) không qua màn hoàn tất thông tin. */
export function isAdminTenant(tenantType?: string | null) {
  return tenantType === 'super_admin' || tenantType === 'admin'
}

/**
 * Những trang vẫn mở khi hồ sơ chưa đủ: chính màn hoàn tất, Cài đặt (khai kho,
 * ngân hàng, bảo mật), Thông báo và Góp ý. Đăng nhập / mã xác nhận / đăng xuất nằm ngoài
 * khung ứng dụng nên không đi qua màn chặn.
 */
export function isProfileGateExempt(pathname: string) {
  return (
    pathname === '/onboarding' ||
    pathname === '/settings' ||
    pathname.startsWith('/settings/') ||
    pathname === '/notifications' ||
    pathname === '/feedback'
  )
}

export function isProfileIncompleteError(err: unknown): err is ApiError {
  return err instanceof ApiError && err.code === 'profile.incomplete'
}

function incompleteItems(err: ApiError): ProfileItem[] {
  const d = err.details as { items?: unknown } | undefined
  return Array.isArray(d?.items) ? (d!.items as ProfileItem[]) : []
}

/**
 * Xử lý lỗi 409 `profile.incomplete` từ các API tạo đơn / nhập đơn / tạo cấp dưới:
 * báo lỗi kèm nút "Hoàn tất thông tin" dẫn về đúng bước còn thiếu.
 * Trả về `true` nếu đã xử lý (nơi gọi không cần báo lỗi nữa).
 */
export function useProfileIncompleteHandler() {
  const nav = useNavigate()
  const qc = useQueryClient()
  return useCallback(
    (err: unknown): boolean => {
      if (!isProfileIncompleteError(err)) return false
      const items = incompleteItems(err)
      const target = items[0]?.key ? `/onboarding?step=${items[0].key}` : '/onboarding'
      const missing = items.map((i) => i.label.toLowerCase()).join(', ')
      const auth = getAuthState()
      const isStaff = auth.shops.find((x) => x.shopId === auth.activeShopId)?.role === 'staff'
      // Nhân viên không tự khai hồ sơ của đơn vị — nhờ chủ (docs/KE_HOACH_THANH_VIEN_DON_VI.md)
      toast.error(isStaff ? 'Đơn vị chưa đủ thông tin' : 'Tài khoản chưa đủ thông tin', {
        description: isStaff
          ? missing ? `Còn thiếu: ${missing}.` : undefined
          : items.length
            ? `Còn thiếu: ${missing}.`
            : err.message,
        duration: 12_000,
        ...(isStaff ? {} : { action: { label: 'Hoàn tất thông tin', onClick: () => nav(target) } }),
      })
      // Màn chặn đọc lại trạng thái — đang lệch với server thì sẽ tự đưa về /onboarding.
      qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY })
      return true
    },
    [nav, qc],
  )
}
